import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { GoogleMapsProvider } from './contexts/GoogleMapsContext';
import { BrandingProvider } from './components/common/BrandingProvider';
import { PublicRoute, RegisteringVendorRoute, ExistingVendorRoute } from './components/common/RouteProtection';
import { useAuth } from './hooks/useAuth';
import Layout from './components/common/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import VendorRegistration from './pages/VendorRegistration';
import Dashboard from './pages/Dashboard';
import Services from './pages/Services';
import Locations from './pages/Locations';
import ServiceRates from './pages/ServiceRates';
import Invoices from './pages/Invoices';
import { hydrateLocalizationDefaults } from './utils/hydrateLocalization';

// Default Route Handler - redirects based on user type
const DefaultRouteHandler = () => {
    const { isExistingVendor, isRegisteringVendor, loading } = useAuth();
    
    // Wait for auth to load before making routing decisions
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }
    
    if (isExistingVendor()) {
        return <Navigate to="/dashboard" replace />;
    } else if (isRegisteringVendor()) {
        return <Navigate to="/vendor-registration" replace />;
    } else {
        return <Navigate to="/login" replace />;
    }
};

// App Routes Component (needs to be inside AuthProvider)
const AppRoutes = () => {
    return (
        <Routes>
            {/* Public Routes - accessible to everyone */}
            <Route 
                path="/login" 
                element={
                    <PublicRoute>
                        <Login />
                    </PublicRoute>
                } 
            />
            <Route 
                path="/register" 
                element={
                    <PublicRoute>
                        <Register />
                    </PublicRoute>
                } 
            />
            
            {/* Registering Vendor Routes - only during registration process */}
            <Route 
                path="/vendor-registration" 
                element={
                    <RegisteringVendorRoute>
                        <VendorRegistration />
                    </RegisteringVendorRoute>
                } 
            />
            
            {/* Existing Vendor Routes - only for authenticated existing vendors */}
            <Route 
                path="/" 
                element={
                    <ExistingVendorRoute>
                        <Layout />
                    </ExistingVendorRoute>
                }
            >
                <Route index element={<DefaultRouteHandler />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="services" element={<Services />} />
                <Route path="locations" element={<Locations />} />
                <Route path="service-rates" element={<ServiceRates />} />
                <Route path="invoices" element={<Invoices />} />
            </Route>
            
            {/* Catch All Route */}
            <Route path="*" element={<DefaultRouteHandler />} />
        </Routes>
    );
};

function App() {
    useEffect(() => {
        hydrateLocalizationDefaults();
    }, []);

    return (
        <BrandingProvider>
            <AuthProvider>
                <GoogleMapsProvider>
                    <Router basename={process.env.PUBLIC_URL || '/'}>
                        <div className="App">
                            <AppRoutes />
                        </div>
                    </Router>
                </GoogleMapsProvider>
            </AuthProvider>
        </BrandingProvider>
    );
}

export default App; 